import type { PatternPreset } from '~/types/presets';
import type { DevicePattern } from '~/types/midi';
import musicData from '~/data/musicData';
import { PATTERNS, type PatternParts } from '~/types/pattern';

/**
 * "OG" Pattern preset for YO! FM Synth
 *
 * This preserves the original default pattern from src/config/defaults/fmsynth.json
 */

const ogFMPattern: DevicePattern = {
  rootNote: musicData.rootNotes.find(n => n.note === 'G♯') || musicData.rootNotes[0],
  scale: musicData.scales.find(s => s.id === 'pentatonic-min') || musicData.scales[0],
  scaleMode: 'Minor',
  pattern: PATTERNS.find(p => p.name === 'AABAAC') || PATTERNS[0],
  patternParts: {
    A: [
      { note: 'A#2', duration: '2n' },
      { note: 'C#2', duration: '2n' },
    ],
    B: [
      { note: 'G#2', duration: '2n' },
      { note: 'B2', duration: '2n' },
    ],
    C: [
      { note: 'B2', duration: '2n' },
      { note: 'G#2', duration: '2n' },
    ],
    D: [
      { note: 'G#2', duration: '2n' },
      { note: 'C#2', duration: '2n' },
    ],
  } as PatternParts,
  beatsPerBar: 4,
  tempo: 110,
  swing: 0,
  space: 30,
  spaceMin: 20,
  spaceMax: 50,
  dissonance: 0,
  dissonanceMin: 0,
  dissonanceMax: 2,
  spread: 12,
  spreadMin: 0,
  spreadMax: 24,
  range: 24,
  rangeMin: 12,
  rangeMax: 37,
  scrambleToggles: {
    scrambleStructure: false,
    scrambleRootNote: false,
    scrambleMode: false,
    scrambleScale: false,
    scrambleSpace: false,
    scrambleDissonance: false,
    scrambleSpread: false,
    scrambleRange: false,
  },
};

/**
 * "OG" FM Synth Pattern Preset
 */
export const OG_FM_PATTERN_PRESET: PatternPreset = {
  id: 'og-fm-pattern',
  name: 'OG',
  pattern: ogFMPattern,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "OG" FM Synth pattern preset
 * Returns a deep copy to avoid mutation
 */
export function getOGFMPattern(): PatternPreset {
  return {
    ...OG_FM_PATTERN_PRESET,
    pattern: {
      ...OG_FM_PATTERN_PRESET.pattern,
      pattern: { ...OG_FM_PATTERN_PRESET.pattern.pattern },
      patternParts: Object.fromEntries(
        Object.entries(OG_FM_PATTERN_PRESET.pattern.patternParts).map(([key, value]) => [
          key,
          value.map(note => ({ ...note })),
        ])
      ) as PatternParts,
      scrambleToggles: { ...OG_FM_PATTERN_PRESET.pattern.scrambleToggles },
    },
  };
}
